import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/me/favorites");
      setFavorites(data || []);
    } catch (err) {
      logError("[AuthContext] failed to fetch favorites:", err);
      setFavorites([]);
    }
  }, []);

  // Handle session_id in URL (from Emergent Google Auth redirect)
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const sid = params.get("session_id");
      if (sid) {
        api.post("/auth/session", { session_id: sid })
          .then((r) => {
            setUser(r.data);
            setLoading(false);
            window.history.replaceState({}, "", window.location.pathname);
          })
          .catch(() => { refreshMe(); });
        return;
      }
    }
    refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    if (user) refreshFavorites();
    else setFavorites([]);
  }, [user, refreshFavorites]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setFavorites([]);
  };

  const toggleFavorite = async (dogId) => {
    if (!user) { login(); return; }
    try {
      const { data } = await api.post("/me/favorites/toggle", { dog_id: dogId });
      // Optimistically fetch updated list
      refreshFavorites();
      return data;
    } catch (err) {
      logError("[AuthContext] toggleFavorite failed:", err);
    }
  };

  const isFavorite = (dogId) => favorites.some((d) => d.id === dogId);

  const value = useMemo(
    () => ({ user, loading, login, logout, favorites, toggleFavorite, isFavorite, refreshFavorites }),
    // login/logout/toggleFavorite/isFavorite are stable closures over user/favorites; refreshFavorites is memoized
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, favorites, refreshFavorites]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
